import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, DownloadSimple, LockKeyOpen } from "@phosphor-icons/react";
import { TaxShell, StatusBadge, Section, cad, errToast } from "@/components/tax/shared";
import { Flags } from "@/components/tax/ReviewStep";
import { Timeline } from "@/components/tax/StatusPanel";
import { PreparerActions } from "@/components/tax/PreparerActions";

function Taxpayer({ c }) {
  const [sin, setSin] = useState(null);
  const reveal = async () => {
    try { setSin((await api.post(`/preparer/cases/${c.id}/reveal-sin`)).data.sin); } catch (e) { errToast(e); }
  };
  const p = c.personal;
  return (
    <Section title="Taxpayer" testId="prep-taxpayer">
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div><p className="text-xs text-slate-500">Legal name</p><p className="font-semibold">{p.legal_name}</p></div>
        <div><p className="text-xs text-slate-500">SIN</p>
          <p className="font-semibold font-mono" data-testid="prep-sin">{sin || p.sin_masked || "—"}
            {!sin && p.sin_masked && <Button size="sm" variant="ghost" className="ml-2" data-testid="prep-reveal-sin-btn" onClick={reveal}><LockKeyOpen className="h-4 w-4 mr-1" /> Reveal (logged)</Button>}</p></div>
        <div><p className="text-xs text-slate-500">Date of birth</p><p className="font-semibold">{p.dob || "—"}</p></div>
        <div><p className="text-xs text-slate-500">Address</p><p className="font-semibold">{[p.address, p.city, p.postal_code].filter(Boolean).join(", ") || "—"}</p></div>
        <div><p className="text-xs text-slate-500">Marital status</p><p className="font-semibold capitalize">{(c.answers.marital_status || "").replace("_", "-")}</p></div>
        <div><p className="text-xs text-slate-500">Dependants</p><p className="font-semibold">{(c.answers.dependants || []).length}</p></div>
      </div>
      {c.answers.notes && <p className="mt-3 text-sm bg-slate-50 rounded p-3"><strong>Customer notes:</strong> {c.answers.notes}</p>}
    </Section>
  );
}

function Requests({ c }) {
  const reqs = c.requests || [];
  if (!reqs.length) return null;
  return (
    <Section title="Information requests" testId="prep-requests">
      {[...reqs].reverse().map((q) => (
        <div key={q.id} className="border-b border-slate-100 last:border-0 py-3 text-sm" data-testid={`prep-request-${q.id}`}>
          <p><strong>You asked:</strong> {q.message} <span className="text-xs text-slate-500">· {new Date(q.at).toLocaleString()}</span></p>
          {q.resolved ? <p className="mt-1 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid={`prep-request-response-${q.id}`}><strong>Customer replied:</strong> {q.response} <span className="text-xs text-slate-500">· {q.responded_at ? new Date(q.responded_at).toLocaleString() : ""}</span></p>
            : <p className="mt-1 text-xs font-semibold text-red-600">Awaiting customer response</p>}
        </div>
      ))}
    </Section>
  );
}

function Docs({ c, labels }) {
  const view = async (d) => {
    try { const r = await api.get(`/preparer/cases/${c.id}/documents/${d.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank", "noopener"); }
    catch (e) { errToast(e); }
  };
  return (
    <Section title={`Documents (${c.documents.length})`} testId="prep-documents">
      {c.documents.map((d) => (
        <div key={d.id} className="border-b border-slate-100 last:border-0 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-black text-red-600 bg-red-50 px-2 py-1 rounded">{d.slip_type}</span>
            <span className="text-sm font-semibold">{d.original_filename || "Manual entry"}</span>
            <span className={`text-xs ${d.verified ? "text-emerald-700" : "text-orange-700"}`}>{d.verified ? "Customer-verified" : "Unverified"}</span>
            {d.has_file && <Button size="sm" variant="ghost" className="ml-auto" data-testid={`prep-doc-view-${d.id}`} onClick={() => view(d)}><Eye className="h-4 w-4" /></Button>}
          </div>
          <div className="grid sm:grid-cols-3 gap-x-4 mt-2 text-xs text-slate-600">
            {Object.entries(d.fields || {}).filter(([, v]) => v !== null && v !== false).map(([k, v]) => <p key={k}><span className="text-slate-400">{labels?.[d.slip_type]?.fields.find((f) => f.key === k)?.label || k}:</span> {labels?.[d.slip_type]?.fields.find((f) => f.key === k)?.type === "money" ? cad(v) : String(v)}</p>)}
          </div>
        </div>
      ))}
    </Section>
  );
}

export default function PreparerCase() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: c, isLoading, error } = useQuery({ queryKey: ["preparer-case", id], queryFn: async () => (await api.get(`/preparer/cases/${id}`)).data, retry: false });
  const { data: meta } = useQuery({ queryKey: ["tax-meta"], queryFn: async () => (await api.get("/tax/meta")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["preparer-case", id] });
  const exportJson = async () => {
    try {
      const r = await api.get(`/preparer/cases/${id}/export`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `T1-${c.tax_year}-${id.slice(-6)}.json`; a.click();
    } catch (e) { errToast(e); }
  };
  if (isLoading) return <TaxShell testId="prep-case-loading"><p className="text-slate-500">Loading…</p></TaxShell>;
  if (error) return <TaxShell testId="prep-case-error"><p className="text-red-600">Case not found or not shared with you.</p></TaxShell>;
  const calc = c.calc || {};
  return (
    <TaxShell testId="preparer-case-page">
      <Link to="/preparer" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-red-600 mb-4" data-testid="prep-back-link"><ArrowLeft className="h-4 w-4" /> All cases</Link>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">{c.personal.legal_name} · {c.tax_year}</h1>
          <p className="text-sm text-slate-500">Customer estimate: {calc.balance_owing > 0 ? `balance owing ${cad(calc.balance_owing)}` : `refund ${cad(calc.refund)}`} · rules v{c.calc_rules_version}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={c.status} testId="prep-case-status" />
          <Button variant="outline" className="rounded-full" data-testid="prep-export-btn" onClick={exportJson}><DownloadSimple className="h-4 w-4 mr-1.5" /> Export data package</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 space-y-5">
          <Taxpayer c={c} />
          <Section title="Flags for review"><Flags flags={calc.flags} /></Section>
          <Requests c={c} />
          <Docs c={c} labels={meta?.slip_types} />
          <Section title="Additional income items">
            {(c.income_items || []).length === 0 ? <p className="text-sm text-slate-500">None</p> : c.income_items.map((i) => (
              <p key={i.id} className="text-sm py-1">{i.label}: <strong>{cad(i.amount)}</strong> — {i.include ? "added to income" : "customer says already on a slip"}</p>
            ))}
          </Section>
        </div>
        <div className="lg:col-span-2 space-y-5">
          <PreparerActions c={c} refresh={refresh} />
          <Section title="Activity"><Timeline items={c.timeline} /></Section>
        </div>
      </div>
    </TaxShell>
  );
}
